import { useEffect, useState } from 'react';
import { api } from '../api/client.js';
import { EVENT_TYPES } from '../lib/format.js';

/**
 * "Ich bin dabei" — der Knopf am Event und die Bilanz danach.
 *
 * Gespeichert wird nur die Zeitspanne. Die Zahlen kommen aus den Scans, die
 * ohnehin laufen. Deshalb verspricht die Karte auch nichts, was sie nicht
 * halten kann: Solange kein Stand erfasst ist, steht hier "bitte scannen"
 * und keine erfundene Null.
 */
export default function EventTeilnahme({ eventSlug, eventTitel }) {
  const [daten, setDaten] = useState(null);
  const [laedt, setLaedt] = useState(false);
  const [fehler, setFehler] = useState(null);

  const holen = () =>
    api.teilnahme.uebersicht(eventSlug).then(setDaten).catch((e) => setFehler(e.message));

  useEffect(() => { holen(); }, [eventSlug]);

  const handeln = async (fn) => {
    setLaedt(true);
    setFehler(null);
    try {
      await fn();
      await holen();
    } catch (e) {
      setFehler(e.message);
    } finally {
      setLaedt(false);
    }
  };

  if (!daten) return <div className="skeleton" style={{ height: 90 }} />;

  const laufend = daten.laufend;
  const dieses = laufend && laufend.eventSlug === eventSlug;
  const frueher = daten.zuDiesemEvent ?? [];

  return (
    <section className="section">
      <h2 className="section__title">Bist du dabei?</h2>

      <div className="teilnahme">
        {dieses ? (
          <>
            <div className="teilnahme__lauf">
              <span className="punkt-live" /> Läuft seit {uhrzeit(laufend.startAt)}
            </div>

            <Zwischenstand bilanz={laufend} />

            <button
              className="btn btn--primary"
              disabled={laedt}
              onClick={() => handeln(() => api.teilnahme.beenden())}
            >
              Event beendet
            </button>
            <p className="teilnahme__fuss">
              Danach einmal die Aktivitätsstatistik im Spiel scannen — daraus
              entsteht die Bilanz.
            </p>
          </>
        ) : laufend ? (
          <p className="teilnahme__fuss">
            Gerade läuft „{laufend.titel}“. Erst das beenden, dann geht das hier.
          </p>
        ) : (
          <>
            <button
              className="btn btn--primary"
              disabled={laedt}
              onClick={() => handeln(() => api.teilnahme.starten({ event: eventSlug }))}
            >
              Ich bin dabei
            </button>
            <p className="teilnahme__fuss">
              Jetzt einmal die Aktivitätsstatistik scannen — das ist der
              Startstand. Am Ende noch einmal, und du siehst, was das Event
              gebracht hat.
            </p>
          </>
        )}

        {fehler && <div className="error" style={{ marginTop: 10 }}>{fehler}</div>}
      </div>

      {frueher.length > 0 && (
        <div className="stack" style={{ marginTop: 14 }}>
          <p className="card__meta">
            {frueher.length === 1 ? 'Einmal' : frueher.length + '-mal'} warst du
            schon bei „{eventTitel}“ dabei:
          </p>
          {frueher.map((b) => <BilanzKarte key={b.id} bilanz={b} knapp />)}
        </div>
      )}
    </section>
  );
}

/** Was bis jetzt zusammengekommen ist, solange die Teilnahme läuft. */
function Zwischenstand({ bilanz }) {
  if (!bilanz.zuwachs) {
    return <p className="teilnahme__fuss">{bilanz.hinweise[0] ?? 'Noch kein Stand erfasst.'}</p>;
  }

  const z = bilanz.zuwachs;

  return (
    <div className="teilnahme__werte">
      <Wert zahl={z.gefangen} label="gefangen" />
      <Wert zahl={z.pokestops} label="Stops" />
      <Wert zahl={(z.distanzM / 1000).toFixed(1)} label="km" />
      {bilanz.faenge.shiny > 0 && <Wert zahl={bilanz.faenge.shiny} label="Shiny gescannt" />}
    </div>
  );
}

function Wert({ zahl, label }) {
  const text = typeof zahl === 'number' ? zahl.toLocaleString('de-DE') : String(zahl);

  return (
    <div className="teilnahme__wert">
      <strong>{'+' + text}</strong>
      <span>{label}</span>
    </div>
  );
}

const STUFEN = {
  stark:    'var(--verdict-strong)',
  gut:      'var(--verdict-solid)',
  normal:   'var(--verdict-collection)',
  schwach:  'var(--text-muted)',
  gemessen: 'var(--verdict-collection)',
  unklar:   'var(--text-muted)',
  laeuft:   'var(--verdict-strong)',
};

/**
 * Eine Bilanz als Karte.
 *
 * Die Reihenfolge ist Absicht: erst das Urteil, dann die Zahlen. Wer
 * zurückblättert, will wissen, ob sich das Event gelohnt hat — die Zahlen
 * sind die Begründung, nicht die Antwort.
 */
export function BilanzKarte({ bilanz, knapp = false, onNotiz }) {
  const [notiz, setNotiz] = useState(bilanz.notiz ?? '');
  const [offen, setOffen] = useState(false);
  const [warum, setWarum] = useState(false);

  const farbe = STUFEN[bilanz.urteil.stufe] ?? 'var(--text-muted)';
  const akzent = bilanz.akzent || farbe;
  const z = bilanz.zuwachs;
  const beute = bilanz.faenge.arten ?? [];

  return (
    <article className="bilanz" style={{ '--urteil-farbe': farbe, '--bilanz-akzent': akzent }}>
      <div className="bilanz__kopf">
        <div className="bilanz__kopf-text">
          <div className="bilanz__marken">
            <span className="badge" style={{ '--badge-color': akzent }}>
              {EVENT_TYPES[bilanz.eventType] ?? 'Event'}
            </span>
            <span className="card__meta">{datumSpanne(bilanz)}</span>
          </div>
          {!knapp && <div className="bilanz__titel">{bilanz.titel}</div>}
        </div>

        {/* Die Medaille ist der Grund, warum man diese Seite herzeigt. Steht
            kein Faktor zur Verfuegung, tritt die Ausbeute an ihre Stelle —
            leer bleibt sie nie. */}
        <div className="bilanz__medaille">
          {bilanz.faktor !== null ? (
            <>
              <strong>{bilanz.faktor.toLocaleString('de-DE')}×</strong>
              <span>der Norm</span>
            </>
          ) : (
            <>
              <strong>{bilanz.faenge.gescannt || '–'}</strong>
              <span>bewertet</span>
            </>
          )}
        </div>
      </div>

      {beute.length > 0 && (
        <div className="bilanz__beute">
          {beute.map((a) => (
            <span key={a.art} className="beute-chip">
              <b>{a.anzahl}×</b> {a.art}
              {a.shiny > 0 && <i className="beute-chip__shiny">Shiny</i>}
              {a.bestesIv !== null && (
                <em>{a.bestesIv.toLocaleString('de-DE')} %</em>
              )}
            </span>
          ))}
        </div>
      )}

      {z && (
        <div className="teilnahme__werte">
          <Wert zahl={z.gefangen} label="gefangen" />
          <Wert zahl={z.pokestops} label="Stops" />
          <Wert zahl={(z.distanzM / 1000).toFixed(1)} label="km" />
          <Wert zahl={z.ep} label="EP" />
        </div>
      )}

      {bilanz.bonbons.length > 0 && (
        <div className="bilanz__bonbons">
          {bilanz.bonbons.slice(0, knapp ? 3 : 8).map((b) => (
            <span key={b.art} className="badge badge--soft">
              {b.art} +{b.bonbon ?? 0}
              {b.megaEnergie ? ' · +' + b.megaEnergie + ' Energie' : ''}
            </span>
          ))}
        </div>
      )}

      <p className="bilanz__urteil">{bilanz.urteil.text}</p>

      {/* Die Einschraenkungen bleiben abrufbar, drueckt sich aber nicht mehr
          vor die Auszeichnung. Wer sie sehen will, klappt sie auf. */}
      {bilanz.hinweise.length > 0 && (
        warum ? (
          <div className="bilanz__warum">
            {bilanz.hinweise.map((h, i) => <p key={i}>{h}</p>)}
            <button className="bilanz__warum-schalter" onClick={() => setWarum(false)}>
              Einklappen
            </button>
          </div>
        ) : (
          <button className="bilanz__warum-schalter" onClick={() => setWarum(true)}>
            Warum stehen hier keine Fangzahlen?
          </button>
        )
      )}

      {onNotiz && (
        offen ? (
          <div className="inline">
            <input
              className="input"
              value={notiz}
              placeholder="Was war los? Community-Day-Attacke, Glücksfänge, Wetter …"
              onChange={(e) => setNotiz(e.target.value)}
            />
            <button
              className="btn btn--klein"
              onClick={() => { onNotiz(bilanz.id, notiz); setOffen(false); }}
            >
              Merken
            </button>
          </div>
        ) : bilanz.notiz ? (
          <p className="bilanz__notiz" onClick={() => setOffen(true)}>„{bilanz.notiz}“</p>
        ) : (
          <button className="btn btn--ghost btn--klein" onClick={() => setOffen(true)}>
            Notiz hinzufügen
          </button>
        )
      )}
    </article>
  );
}

function uhrzeit(wert) {
  return new Date(wert.replace(' ', 'T')).toLocaleTimeString('de-DE',
    { hour: '2-digit', minute: '2-digit' });
}

function datumSpanne(bilanz) {
  const d = new Date(bilanz.startAt.replace(' ', 'T'));
  const datum = d.toLocaleDateString('de-DE', { day: '2-digit', month: 'short', year: 'numeric' });
  const dauer = bilanz.proStunde
    ? bilanz.proStunde.stunden.toLocaleString('de-DE') + ' h gemessen'
    : bilanz.dauerH.toLocaleString('de-DE') + ' h';

  return datum + ' · ' + dauer;
}
